import { useEffect } from 'preact/hooks';
import { ledger, type PaperTrade } from './ledger';
import type { AssetClass } from '@ordo-mentis/ledger-store';
import { dataHub } from './datahub';
import { CAESAR_CAPS, isAutoTrade, underDailyAutoCap } from './caesar-caps';

// Caps + the daily-cap math live in ./caesar-caps.ts (dependency-free, so the
// node test suite can exercise them directly). Re-exported here so the nine
// existing importers of CAESAR_CAPS keep working unchanged.
export { CAESAR_CAPS, underDailyAutoCap };

// Caesar auto-PAPER-trade (formerly To'mas): when the operator enables it, Caesar logs
// eligible calculator setups to HIS OWN paper account (notes "auto paper" — the
// Caesar/Operator split in ledgerStats). Paper automation is clamped to the Caesar policy
// caps; live execution remains Owner-Only through the separate arm flow. Settings persist in
// localStorage; the engine de-dupes by open position so the same setup isn't double-logged.

export interface CaesarAutoTradeSettings {
  enabled: boolean;
  startingCapital: number; // Caesar's own paper account, default $100,000
  riskPct: number; // % of his account risked per trade (recorded on the trade)
  maxOpenPositions: number; // clamped to CAESAR_CAPS.maxOpenPositions
  strongOnly: boolean; // only auto-log the strongest verdicts (vs any eligible one)
  mirrorToAlpaca: boolean; // ALSO place the setup on the REAL Alpaca PAPER account (local hub only)
}

// Active persisted key is the Caesar name; the legacy To'mas key migrates
// forward on first read (copied to the new key, old key left in place for
// rollback — same non-destructive pattern as migrateLegacy in
// @ordo-mentis/ledger-store, see lib/ledger.ts).
const KEY = 'ordoCaesarAutoTrade_v1';
const LEGACY_KEY = 'ordoTomasAutoTrade_v1';
const DEFAULTS: CaesarAutoTradeSettings = {
  enabled: false,
  startingCapital: 100000,
  riskPct: 1,
  maxOpenPositions: 3,
  strongOnly: true,
  mirrorToAlpaca: false,
};

export function clampSettings(s: Partial<CaesarAutoTradeSettings>): CaesarAutoTradeSettings {
  return {
    enabled: Boolean(s.enabled),
    startingCapital: Math.max(1, Math.round(Number(s.startingCapital) || DEFAULTS.startingCapital)),
    riskPct: Math.min(5, Math.max(0.1, Number(s.riskPct) || DEFAULTS.riskPct)),
    maxOpenPositions: Math.min(CAESAR_CAPS.maxOpenPositions, Math.max(1, Math.floor(Number(s.maxOpenPositions) || 1))),
    strongOnly: s.strongOnly !== false,
    mirrorToAlpaca: Boolean(s.mirrorToAlpaca),
  };
}

export function getSettings(): CaesarAutoTradeSettings {
  try {
    if (typeof localStorage === 'undefined') return { ...DEFAULTS };
    let raw = localStorage.getItem(KEY);
    if (raw == null) {
      // Migrate-on-read from the legacy To'mas key: read old if new absent,
      // write the new key, LEAVE the old key in place (safe rollback).
      raw = localStorage.getItem(LEGACY_KEY);
      if (raw != null) {
        try {
          localStorage.setItem(KEY, raw);
        } catch {
          /* ignore */
        }
      }
    }
    if (raw) return clampSettings({ ...DEFAULTS, ...JSON.parse(raw) });
  } catch {
    /* ignore */
  }
  return { ...DEFAULTS };
}

export function setSettings(s: Partial<CaesarAutoTradeSettings>): CaesarAutoTradeSettings {
  const c = clampSettings(s);
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(KEY, JSON.stringify(c));
  } catch {
    /* ignore */
  }
  return c;
}

const isAuto = isAutoTrade;
const isOpen = (t: PaperTrade) => t.status === 'open' || (t.status !== 'closed' && t.exitPrice == null);

export function caesarOpenTrades(): PaperTrade[] {
  return ledger.allTrades().filter((t) => isAuto(t) && isOpen(t));
}
export function caesarTradeCount(): number {
  return ledger.allTrades().filter(isAuto).length;
}

export interface AutoCandidate {
  symbol: string;
  type: string; // "call 195", "long", direction label
  entry: number;
  eligible: boolean; // the verdict is one Caesar would take
  strong: boolean; // the strongest tier (A / APPROVE / Favorable / Balanced)
  extra?: Record<string, unknown>; // stop/target/contracts/etc. carried onto the row
}

// Only EQUITY (day-trade) setups map cleanly to an Alpaca order: the candidate symbol IS a
// tradeable stock and the entry IS its share price. Options/forex/commodities carry an underlying
// or pair symbol with a premium/quote that would build a MALFORMED equity order, so the mirror and
// export skip them (the local ledger still simulates them — see ledgerStats' Caesar split).
export const ALPACA_MIRRORABLE_ASSETS: AssetClass[] = ['daytrade'];
export function isAlpacaMirrorable(asset: AssetClass): boolean {
  return ALPACA_MIRRORABLE_ASSETS.includes(asset);
}

// Map an equity setup's direction label to an order side. Default 'buy' (entries are long); an
// explicit short / bearish / "-" label becomes 'sell'. Only used for day-trade equities, whose
// labels are long/short; option put/call labels stay on their own simulated paper path.
export function sideForCandidate(c: AutoCandidate): 'buy' | 'sell' {
  const t = `${c.type || ''}`.toLowerCase().trim();
  return /\b(short|sell|bear)\b/.test(t) || t.startsWith('-') ? 'sell' : 'buy';
}

// Compute a paper-cap-fitting share qty for an equity entry, or null if a single share already
// exceeds the per-trade dollar cap (the order would only be refused server-side).
export function mirrorQty(entry: number): number | null {
  if (!(Number.isFinite(entry) && entry > 0) || entry > CAESAR_CAPS.maxTradeDollars) return null;
  return Math.max(1, Math.floor(CAESAR_CAPS.maxTradeDollars / entry));
}

// Best-effort mirror of one EQUITY Caesar setup onto the REAL Alpaca PAPER account (local DataHub
// only). The server path is hardcoded to the Alpaca paper host and clamps to the
// Caesar caps; live execution stays on the separate Owner-Only arm flow. It settles best-effort
// so the local ledger append still records the setup when broker paper mirroring is unavailable.
// Callers must already have screened the asset (isAlpacaMirrorable); this guards price + hub.
export function mirrorToAlpacaPaper(c: AutoCandidate): Promise<void> {
  try {
    if (!dataHub.isLocal()) return Promise.resolve();
    if (!(c.symbol && Number.isFinite(c.entry) && c.entry > 0)) return Promise.resolve();
    const qty = mirrorQty(c.entry);
    if (qty == null) return Promise.resolve(); // a single share exceeds the $100 paper cap
    return dataHub
      .placePaperOrder({ symbol: c.symbol, side: sideForCandidate(c), qty, limitPrice: c.entry })
      .then(() => undefined)
      .catch(() => undefined);
  } catch {
    return Promise.resolve();
  }
}

// Append a paper trade to Caesar's account if his auto-trade is on, the setup is eligible,
// he's under his open-position cap (clamped to the Caesar caps), and the same open setup isn't
// already logged. Returns true if it logged. Paper path. When mirrorToAlpaca is on (and a local
// DataHub is present), the same setup is ALSO submitted to the Alpaca PAPER account — fire-and-
// forget, so a broker paper hiccup leaves the local ledger intact.
export function maybeAutoPaperTrade(asset: AssetClass, c: AutoCandidate | null): boolean {
  if (!c || !c.eligible) return false;
  const s = getSettings();
  if (!s.enabled) return false;
  if (s.strongOnly && !c.strong) return false;
  if (!(c.symbol && Number.isFinite(c.entry) && c.entry > 0)) return false;
  // de-dupe: don't re-open an identical (symbol, type) Caesar position
  const dup = caesarOpenTrades().some((t) => t.symbol === c.symbol && t.type === c.type);
  if (dup) return false;
  if (caesarOpenTrades().length >= Math.min(s.maxOpenPositions, CAESAR_CAPS.maxOpenPositions)) return false;
  // Daily-trade cap: the Settings panel advertises this guardrail, so it must bind.
  if (!underDailyAutoCap(ledger.allTrades(), CAESAR_CAPS.maxTradesPerDay)) return false;
  ledger.appendTrade(asset, {
    // Persisted trade-id prefix stays 'tomas-' — persisted-data formats are wave-3 territory.
    id: `tomas-${asset}-${Date.now()}`,
    symbol: c.symbol,
    type: c.type,
    entry: c.entry,
    status: 'open',
    notes: `auto paper - Caesar (risk ${s.riskPct}% of $${s.startingCapital.toLocaleString()})`,
    recordedAt: new Date().toISOString(),
    ...(c.extra || {}),
  } as PaperTrade);
  // Mirror to the real Alpaca PAPER account only for equity (day-trade) setups — see
  // isAlpacaMirrorable. Paper path, fire-and-forget so a broker hiccup leaves the ledger intact.
  if (s.mirrorToAlpaca && isAlpacaMirrorable(asset)) void mirrorToAlpacaPaper(c);
  return true;
}

// Calculator hook: debounce so Caesar evaluates the SETTLED setup (not every keystroke),
// then auto-logs it if eligible. No-op unless auto-trade is enabled in Settings.
export function useCaesarAutoTrade(asset: AssetClass, candidate: AutoCandidate | null): void {
  const key = candidate ? `${candidate.symbol}|${candidate.type}|${candidate.entry}|${candidate.eligible}|${candidate.strong}` : '';
  useEffect(() => {
    if (!candidate || !candidate.eligible) return;
    const id = setTimeout(() => maybeAutoPaperTrade(asset, candidate), 1500);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset, key]);
}
